import { useCallback, useMemo } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, tokenRequest } from './msalConfig';
import type { UserRole } from '../types/user';

interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  userName: string | undefined;
  userEmail: string | undefined;
  userId: string | undefined;
  roles: UserRole[];
  /**
   * True when the caller has either `Ops.Read` or `Ops.Write`.
   * Use this for markup-side ops-section visibility gating.
   * Backend enforcement remains the security source of truth — this field exists
   * purely for UX layering.
   */
  isOps: boolean;
  /**
   * True when the caller has the `Ops.Write` (or equivalent write-capable) role.
   * Use this for markup-side write-action gating (Approve/Deny buttons, etc.).
   * Backend enforcement remains the security source of truth — this field exists
   * purely for UX layering.
   */
  canOpsWrite: boolean;
  login: () => Promise<void>;
  loginRedirect: () => Promise<void>;
  signup: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

export function useAuth(): UseAuthReturn {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = inProgress !== InteractionStatus.None;

  const account = accounts[0];

  const claims = account?.idTokenClaims;

  const claimName = typeof claims?.name === 'string' ? claims.name : undefined;
  const claimEmail = typeof claims?.email === 'string' ? claims.email : undefined;
  const userName = claimName ?? account?.name;
  const userEmail = claimEmail ?? account?.username;
  const userId = account?.localAccountId;

  const roles = useMemo<UserRole[]>(() => {
    const tokenRoles = claims?.roles ?? [];
    const mapped: UserRole[] = ['user'];
    const hasOpsRead = tokenRoles.includes('Ops.Read');
    const hasOpsWrite = tokenRoles.includes('Ops.Write');
    if (hasOpsRead || hasOpsWrite) {
      mapped.push('ops');
      if (hasOpsRead) {
        mapped.push('ops-read');
      }
      if (hasOpsWrite) {
        mapped.push('ops-write');
      }
    }
    return isAuthenticated ? mapped : [];
  }, [claims?.roles, isAuthenticated]);

  const isOps = roles.includes('ops-read') || roles.includes('ops-write');
  const canOpsWrite = roles.includes('ops-write');

  const login = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) return;
    await instance.loginPopup(loginRequest);
  }, [instance, inProgress]);

  const loginRedirect = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) return;
    await instance.loginRedirect(loginRequest);
  }, [instance, inProgress]);

  const signup = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) return;
    await instance.loginRedirect({
      ...loginRequest,
      extraQueryParameters: { prompt: 'create' },
    });
  }, [instance, inProgress]);

  const logout = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) return;
    await instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance, inProgress]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) {
      throw new Error('No active account. User must sign in.');
    }
    const response = await instance.acquireTokenSilent({
      ...tokenRequest,
      account,
    });
    return response.accessToken;
  }, [instance, account]);

  return {
    isAuthenticated,
    isLoading,
    userName,
    userEmail,
    userId,
    roles,
    isOps,
    canOpsWrite,
    login,
    loginRedirect,
    signup,
    logout,
    getAccessToken,
  };
}
